import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, PaperPlaneTilt, SpinnerGap, Copy, Check, Eye } from "@phosphor-icons/react";

export function StudioSyndicateTab({ api, activePackage }) {
  const [zapierUrl, setZapierUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const buildPackagePayload = () => {
    if (!activePackage?.package_markdown) return null;
    return {
      title: activePackage.idea || activePackage.title || "Untitled",
      content_markdown: activePackage.package_markdown,
      channel_variants: {},
      tags: activePackage.channels || [],
      campaign_name: `studio_${activePackage.package_id || "unknown"}`,
    };
  };

  const handlePreview = async () => {
    if (!zapierUrl.trim()) { toast.error("Enter your Zapier webhook URL"); return; }
    const pkg = buildPackagePayload();
    if (!pkg) { toast.error("Select or create a package first"); return; }
    setLoadingPreview(true);
    try {
      const resp = await api.post("/content-studio/syndication/zapier-preview", { webhook_url: zapierUrl.trim(), package: pkg });
      setPreview(resp.data);
      toast.success("Preview ready");
    } catch (err) { toast.error(err.response?.data?.detail || "Preview failed"); }
    setLoadingPreview(false);
  };

  const handleSend = async () => {
    if (!zapierUrl.trim()) { toast.error("Enter your Zapier webhook URL"); return; }
    const pkg = buildPackagePayload();
    if (!pkg) { toast.error("No package to send"); return; }
    setSending(true);
    try {
      const resp = await api.post("/content-studio/syndication/send", { webhook_url: zapierUrl.trim(), package: pkg });
      if (resp.data.status === "sent") { setSent(true); toast.success("Sent to Zapier!"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send to Zapier"); }
    setSending(false);
  };

  const copyPayload = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Copied!");
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Globe size={20} className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Zapier Syndication</h2>
            <p className="text-xs text-[var(--text-muted)]">Optional — send your package to Zapier for multi-channel publishing</p>
          </div>
        </div>

        {!activePackage ? (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3">
            <p className="text-xs text-amber-400">Create a package in the "Create" tab first, or select one from "History".</p>
          </div>
        ) : (
          <>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3">
              <p className="text-xs text-emerald-400">
                Active package: <span className="font-mono">{activePackage.package_id}</span> — "{(activePackage.idea || "").slice(0, 60)}"
              </p>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1.5">Your Zapier Webhook URL</label>
              <Input value={zapierUrl} onChange={e => setZapierUrl(e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/..."
                className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm" data-testid="studio-zapier-url" />
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                Get this from your Zapier account: Webhooks by Zapier &rarr; Catch Hook
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button onClick={handlePreview} disabled={loadingPreview || !zapierUrl.trim()} variant="outline"
                className="h-10 rounded-xl gap-2 border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="studio-zapier-preview-btn">
                {loadingPreview ? <SpinnerGap size={14} className="animate-spin" /> : <Eye size={14} />} Preview
              </Button>
              <Button onClick={handleSend} disabled={sending || sent || !zapierUrl.trim()} variant="outline"
                className={`h-10 rounded-xl gap-2 ${sent ? "border-emerald-500/30 text-emerald-400" : "border-[#FF4400]/30 text-[#FF4400] hover:bg-[#FF4400]/10"}`} data-testid="studio-zapier-send-btn">
                {sending ? <SpinnerGap size={14} className="animate-spin" /> : sent ? <Check size={14} /> : <PaperPlaneTilt size={14} />}
                {sending ? "Sending..." : sent ? "Sent!" : "Send to Zapier"}
              </Button>
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 space-y-4" data-testid="studio-zapier-output">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">Payload Preview</h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-semibold">{preview.status}</span>
            </div>
            <pre className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-4 text-xs font-mono text-[var(--text-secondary)] overflow-x-auto max-h-[400px] overflow-y-auto whitespace-pre-wrap">
              {JSON.stringify(preview.payload, null, 2)}
            </pre>
            {preview.next_steps && (
              <div className="space-y-2">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Setup Steps</h4>
                <ol className="space-y-1.5">
                  {preview.next_steps.map((step, i) => (
                    <li key={`step-${step.slice(0, 20)}`} className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                      <span className="w-5 h-5 rounded-full bg-[#FF4400]/10 text-[#FF4400] flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <Button onClick={() => copyPayload(JSON.stringify(preview.payload, null, 2))} variant="outline" className="w-full h-9 rounded-lg gap-1.5 text-xs">
              {copied ? <Check size={12} /> : <Copy size={12} />} Copy Payload
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
